import { none } from "ramda";
import React from "react";
import Info from "components/chrome/main_view/info";
import {
  WorkforceBadPermissions,
  WorkforceGettingStarted,
} from "components/chrome/main_view/info_messages";
import PermissionMissingInfo from "components/users/permission_missing_info";
import GroupManagementFlagsMissingInfo from "group_management/components/group_management_flags_missing_info";

import {
  BREADCRUMB_PERMISSIONS,
  hasAnalyticsPermission,
  hasBreadcrumbPermission,
} from "helpers/chrome/locations";
import type { IBrandGroup } from "helpers/transforms/entities";
import type { IEntity } from "interfaces/app";
import { isAuditLogFeatureEnabled } from "navigation/audit_logs_feature_gate";
import { LD_FEATURE_FLAGS } from "types/context";
import type { IVerifyPermissionsExtras } from "types/navigation";

export const userListsPermission = ({ stores }: IBrandGroup) => {
  const hasUsersPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.USERS
  );
  const hasViewUsersRolesPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.VIEW_USERS_ROLES
  );
  const hasViewMerchantUsersPermission =
    hasAnalyticsPermission("viewMerchantUsers");

  if (
    !stores.some(
      (store) =>
        hasUsersPermission(store) ||
        hasViewUsersRolesPermission(store) ||
        hasViewMerchantUsersPermission(store)
    )
  ) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const userUpdatePermission = (
  _: IBrandGroup,
  store: IEntity | undefined
) => {
  const hasManageUserPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.USERS
  );
  if (!store) {
    return undefined;
  }
  if (!hasManageUserPermission(store)) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const userRolesBCPermission = (
  _: IBrandGroup,
  store: IEntity | undefined
) => {
  const hasRoleAccessPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.ROLE_ACCESS
  );
  const hasViewUsersRolesPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.VIEW_USERS_ROLES
  );

  if (!store) {
    return undefined;
  }

  if (!hasRoleAccessPermission(store) && !hasViewUsersRolesPermission(store)) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const userRolesAnalyticsPermission = ({ stores }: IBrandGroup) => {
  if (none(hasAnalyticsPermission("viewMerchantUsers"), stores)) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const workforcePermission = (
  _: IBrandGroup,
  store: IEntity | undefined
) => {
  if (!store) {
    return;
  }

  if (hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.WORKFORCE)(store)) {
    return;
  }

  if (
    hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.USERS)(store) &&
    hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.ROLE_ACCESS)(store)
  ) {
    return (
      <Info title="Getting Started">
        <WorkforceGettingStarted />
      </Info>
    );
  }

  return (
    <Info title="Additional Permissions Required">
      <WorkforceBadPermissions />
    </Info>
  );
};

export const menusPermission = (_: IBrandGroup, store: IEntity | undefined) => {
  const hasMenuPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.MENU
  );
  const hasViewMenusPermission = hasBreadcrumbPermission(
    BREADCRUMB_PERMISSIONS.VIEW_MENUS
  );

  if (!store) {
    return undefined;
  }

  if (!hasMenuPermission(store) && !hasViewMenusPermission(store)) {
    return <PermissionMissingInfo pageName="menus" />;
  }
};

export const GroupManagementPermission = ({ stores }: IBrandGroup) => {
  if (none(hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.MENU), stores)) {
    return <PermissionMissingInfo pageName="Group Management" />;
  }
};

// The LaunchDarkly DASHBOARD keys for the flags the Group Management pages need —
// what the polite provisioning error shows to users/support (the in-app
// names are the client SDK's camelCased forms).
const GROUP_MANAGEMENT_FLAG_LD_KEYS: Record<string, string> = {
  [LD_FEATURE_FLAGS.groupManagement]: "group-management",
};

function missingGroupManagementFlags(
  requiredFlags: string[],
  extras?: IVerifyPermissionsExtras
): string[] {
  return requiredFlags
    .filter((flag) => !extras || !extras.featureFlags[flag])
    .map((flag) => GROUP_MANAGEMENT_FLAG_LD_KEYS[flag] || flag);
}

/**
 * Deep-link gate for the ORIGINAL Group Management pages (menus/items). The
 * sidebar hides them via the section-level requiresFeatureFlag, but a
 * shared/bookmarked URL bypasses the sidebar — show a polite provisioning
 * error naming the missing flag instead of a blank or generic page.
 */
export function groupManagementVerifyPermissions(
  brand: IBrandGroup,
  _store: IEntity | undefined,
  extras?: IVerifyPermissionsExtras
): JSX.Element | undefined {
  const missingFlags = missingGroupManagementFlags(
    [LD_FEATURE_FLAGS.groupManagement],
    extras
  );
  if (missingFlags.length > 0) {
    return <GroupManagementFlagsMissingInfo missingFlags={missingFlags} />;
  }
  return GroupManagementPermission(brand);
}

/**
 * The five Group Management entity pages (categories, courses, modifiers, modifier
 * groups, display groups) have NO legacy brand-level fallback, so they are
 * blocked entirely — deep links included — until the brand is rolled out via
 * group-management (the same LaunchDarkly flag hq_service
 * gates the bulk endpoints on, read via the client SDK's camelCased key).
 * A missing flag shows the polite provisioning error naming exactly what
 * support needs to enable. Menu permission rules match
 * {@link GroupManagementPermission}.
 */
export function groupManagementEntityVerifyPermissions(
  brand: IBrandGroup,
  _store: IEntity | undefined,
  extras?: IVerifyPermissionsExtras
): JSX.Element | undefined {
  const missingFlags = missingGroupManagementFlags(
    [LD_FEATURE_FLAGS.groupManagement],
    extras
  );
  if (missingFlags.length > 0) {
    return <GroupManagementFlagsMissingInfo missingFlags={missingFlags} />;
  }
  return GroupManagementPermission(brand);
}

/**
 * Blocks main-view content (including deep links) when LaunchDarkly or Breadcrumb
 * reports checks fail — same rules as {@link isAuditLogFeatureEnabled}.
 */
export function auditLogsVerifyPermissions(
  _brand: IBrandGroup,
  _store: IEntity | undefined,
  extras?: IVerifyPermissionsExtras
): JSX.Element | undefined {
  if (!extras || !isAuditLogFeatureEnabled(extras.featureFlags, _store)) {
    return <PermissionMissingInfo pageName="audit logs" />;
  }
  return undefined;
}
